import { useState, useRef, useEffect } from "react";
import { LucidePaintbrushVertical, LucideTrash } from "lucide-react";
import { CirclePicker, ChromePicker } from "react-color";
import { MdOutlineColorize } from "react-icons/md";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import TextField from "@mui/material/TextField";
import { deleteConfirmationMessage } from "@/data/constantStrings";

export default function TagsOptions({
  tagsOptions,
  setTagsOptions,
  currentTag,
  setCurrentTag,
  children,
  handleAddCompanyTag,
  handleDeleteTag,
  confirmDelete,
  tagId,
  editingTag,
  setEditingTag,
}) {
  const [open, setOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [colorPickerValue, setColorPickerValue] = useState("");
  const [editingTagName, setEditingTagName] = useState(currentTag);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setEditingTagName(currentTag || "");
  }, [open, currentTag]);

  const handleEditTagNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTagName(e.target.value);
  };

  const handleConfirmTagEdit = () => {
    const safeEditingTag = editingTagName?.trim() || "";
    const safeCurrentTag = currentTag?.trim() || "";

    if (safeEditingTag !== "" && safeEditingTag !== safeCurrentTag) {
      handleAddCompanyTag(safeEditingTag, true, tagId);
      setCurrentTag(safeEditingTag);
    }
  };

  const handleColorPickerSelect = (color: any) => {
    const hex = color.hex || color;

    setColorPickerValue(hex);

    const tagToUpdate = tagsOptions?.find(
      (t) =>
        t.tagId === tagId ||
        t.tagName?.toLowerCase() === currentTag?.toLowerCase()
    );

    if (!tagToUpdate) return;

    setTagsOptions((prev) =>
      prev.map((option) =>
        option.tagId === tagToUpdate.tagId ? { ...option, Colour: hex } : option
      )
    );

    handleAddCompanyTag(tagToUpdate.tagName, true, tagToUpdate.tagId, hex);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirmTagEdit(); // ✅ Enter triggers update
      setOpen(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isDeleting) {
          handleConfirmTagEdit();
        }
        setOpen(isOpen);
        if (isOpen) setIsDeleting(false);
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <span
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          {children}
        </span>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        onClick={(e) => e.stopPropagation()}
        className="z-[9999] w-72 p-3 bg-white border border-gray-200 rounded-md shadow-md dark:bg-gray-800 dark:border-gray-700"
      >
        <TextField
          inputRef={inputRef}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Edit tag name..."
          value={editingTagName || ""}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => {
            const tagToEdit = tagsOptions?.find(
              (t) =>
                t.tagId === tagId ||
                t.tagName?.toLowerCase() === currentTag?.toLowerCase()
            );
            setEditingTag(tagToEdit || null);
          }}
          onChange={handleEditTagNameChange}
          onKeyDown={handleKeyDown}
          onBlur={handleConfirmTagEdit}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": { fontSize: "0.875rem" },
          }}
        />

        <div className="circle-picker-wrapper">
          {showColorPicker && (
            <div>
              <ChromePicker
                className="w-full mb-3"
                color={colorPickerValue}
                onChange={(color) => {
                  setColorPickerValue(color.hex);
                }}
                disableAlpha
              />

              <div className="flex justify-end mt-2">
                <Button
                  className="w-full mt-1 mb-2 text-white bg-blue-600 hover:bg-blue-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColorPickerSelect(colorPickerValue);
                    setShowColorPicker(false);
                  }}
                >
                  <LucidePaintbrushVertical size={14} />
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div className="relative">
            <CirclePicker
              onClick={(e) => e.stopPropagation()}
              className="tags-option-circlepicker"
              onChange={handleColorPickerSelect}
            />
            <MdOutlineColorize
              className="text-gray-600 cursor-pointer hover:text-gray-800 color-picker-icon"
              size={22}
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker((prev) => !prev);
              }}
            />
          </div>
        </div>

        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="destructive"
            className="flex items-center w-full"
            onClick={() => {
              confirmDelete(
                () => handleDeleteTag(tagId),
                deleteConfirmationMessage + "tag"
              );
            }}
          >
            <LucideTrash size={14} className="mr-2" />
            Delete
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
